import { useCallback, useRef, useState } from 'react';

/**
 * 送信(保存)処理の二重起動を防ぐ再入ガード。
 *
 * 背景: 更新ボタンは `isSendable` のみで制御されており、onSave が async のため
 * 実行中も押下可能なままだった。高負荷端末(iPad)では Loading オーバーレイの描画前に
 * 2回目のタップが入り、add/update/delete が多重実行 → kintone のリビジョン/コメント増加・
 * 追加レコードの重複が発生していた(ソフト削除はエラーにならず多重化が止まらない)。
 *
 * 設計:
 * - `runningRef` は同期的に立てる/降ろすため、描画タイミングや端末負荷に依存せず
 *   in-flight 中の2回目以降の呼び出しを確実に弾く(本ガードの本体)。
 * - `isSubmitting` は表示用(ボタンの disabled 等)。React state の1レンダ遅延があるが、
 *   正しさは `runningRef` が担保する。
 *
 * 使い方:
 *   const { isSubmitting, runGuarded } = useSubmitGuard();
 *   const onSave = () => runGuarded(async () => { ...await...; });
 *   <Buttons disabled={!isSendable || isSubmitting} onClick={onSave} ... />
 */
export const useSubmitGuard = () => {
  const runningRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const runGuarded = useCallback(async (task: () => Promise<void>) => {
    if (runningRef.current) return; //実行中なら何もしない(再入防止)
    runningRef.current = true;
    setIsSubmitting(true);
    try {
      await task();
    } finally {
      runningRef.current = false;
      setIsSubmitting(false);
    }
  }, []);

  return { isSubmitting, runGuarded };
};
